/**
 * 3-шаговый мастер заказа обучения (p2-service-training, §6.1-6.5; инверсия D1/A3): клиент
 * формирует состав заказа (программы + сотрудники), а не подаёт лёгкую заявку — состав-
 * ведомость больше не ведёт команда. Шаги: (1) данные компании — снимок/правка реквизитов
 * по паттерну p2-company-profile; (2) программы и сотрудники — по каждой программе выбор
 * сотрудников с группировкой по подразделениям (centrika-docs, MR !18) и массовыми
 * действиями (D7), добавление сотрудника прямо из шага (§10.1, D8) — отдельным экраном того же
 * диалога выбора (centrika-docs, MR !19); (3) подтверждение — таблица выбранных сотрудников
 * перед отправкой.
 *
 * Согласия ПД переехали с шага 3 на экран «Заказ отправлен» (centrika-docs, MR !36): они
 * больше не тормозят отправку заказа — клиент загружает подписанные PDF уже после подачи, а
 * менеджер начинает работу независимо от их комплектности. Тот же экран открывается при
 * повторном заходе в отправленный заказ (мастера в режиме просмотра больше нет). Согласия и
 * документы об образовании сотрудников — shared-компонент `training/employee-documents.tsx`
 * (centrika-docs, MR !40/!59): он же отображается на странице заказа клиента.
 *
 * На шаге 3 «Заполнение данных» образование сотрудника обязательно (centrika-docs!59): выпадающий
 * список сохраняется в карточку, отправка доступна только когда оно заполнено у всех.
 *
 * Каждое действие сохраняется сразу (без отдельной кнопки «Сохранить черновик») — сервер
 * авторитетно хранит состав в training_lines, а согласия — в карточках сотрудников (person_consents).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2, Download, FileSpreadsheet, Search, UserPlus, Users, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  ordersApi,
  trainingApi,
  companyApi,
  ApiError,
  type CompanyProfile,
  type Department,
  type Employee,
  type OrderDataStatus,
  type OrderDetail,
  type OrderImportResult,
  type TrainingCompositionPatch,
  type TrainingDirectionItem,
  type TrainingPackageComposition,
  type TrainingProgramComposition,
  type TrainingProgramEmployee,
  type TrainingSelectedEmployee,
  type TrainingWizard,
  type UpdateCompanyProfileInput,
  type Workplace,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import {
  TRAINING_MASS_ACTION_LABELS,
  TRAINING_STATUS_VIEW,
  applyTrainingMassAction,
  listenerStatus,
  type TrainingMassActionPreset,
} from '@/lib/training'
import { EmployeeDocuments } from '@/training/employee-documents'
import { ProgramTreePicker, focusProgramSearch } from '@/training/program-tree-picker'
import { SelectedPrograms, listenersCount, namedOfPlaces } from '@/training/selected-programs'
import { ListenersCountInput } from '@/training/listeners-count-input'
import { isUnspecifiedDepartment, UNSPECIFIED_DEPARTMENT } from '@/lib/department'
import { TrainingStepData, listenerRowId } from '@/training/training-step-data'
import {
  ORDER_IMPORT_BUTTON,
  ORDER_IMPORT_TEMPLATE_BUTTON,
  OrderImportDialog,
} from '@/orders/order-import-dialog'
import { downloadFromUrl } from '@/lib/download'
import { CompanyRequisitesStep } from '@/company/requisites-step'
import { CreateEmployeeForm } from '@/company/employees-manager'
import { Card } from '@/design-system/components/layout/card'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Input } from '@/design-system/components/primitives/input'
import { Stepper } from '@/design-system/components/data/stepper'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'

// Четыре шага (ТЗ 26.10.2026, п.1.1; training-listener-completeness).
const STEP_LABELS = ['Данные компании', 'Программы и сотрудники', 'Заполнение данных', 'Подтверждение']
/**
 * Физлицо заказывает обучение для себя (individual-training-order): те же четыре шага, первый — его
 * данные вместо реквизитов компании, слушатель — он сам.
 */
const STEP_LABELS_INDIVIDUAL = ['Ваши данные', 'Программы', 'Заполнение данных', 'Подтверждение']

/** Подписи шагов мастера: у физлица — свои. */
export function trainingStepLabels(individual: boolean): string[] {
  return individual ? STEP_LABELS_INDIVIDUAL : STEP_LABELS
}
/** Шаг «Заполнение данных». */
const DATA_STEP = 2
/**
 * Шаг, с которого открывается мастер: заказ «На заполнении» (training-order-filling) — заявка уже у
 * менеджера, клиент возвращается довносить данные слушателей — сразу «Заполнение данных».
 * Экспортируется: страница по нему же ставит шаг в шапке до первого рендера мастера.
 */
export function initialTrainingStep(order: Pick<OrderDetail, 'dataStatus'>): number {
  return order.dataStatus === 'filling' ? DATA_STEP : 0
}
/** Уйти с шага «Заполнение данных» нельзя, пока в карточке слушателя есть некорректное значение. */
export const INVALID_DRAFTS_NOTICE =
  'Исправьте или очистите поля, выделенные красным: некорректное значение не сохраняется.'
/** Подтверждение недоступно: у кого-то из слушателей заполнено не всё. */
export const LISTENERS_INCOMPLETE_NOTICE =
  'Заполните данные всех слушателей: СНИЛС, дату рождения, пол, образование, e-mail, согласие на обработку персональных данных и — где нужен — документ об образовании.'

/** Почему черновик не отправить: в нём программа или пакет, которых больше нет в каталоге. */
const UNAVAILABLE_SELECTION_NOTICE =
  'В заказе есть программы или пакеты, которых больше нет в каталоге. Вернитесь к шагу «Программы и сотрудники» и уберите их из заказа.'
/**
 * Под последним шагом черновика, пока заявка не отправлена (training-order-filling): подтвердить
 * данные можно только после заявки.
 */
export const REQUEST_BEFORE_CONFIRM_HINT =
  'Сначала отправьте заявку менеджеру — он подготовит договор и счёт. Данные слушателей можно заполнять и сейчас: подтвердить их получится после отправки заявки.'
/**
 * Подсказка выбора программ (ТЗ 26.10.2026, п.1.2; ответ заказчицы 04.11 к п.4.1–4.3 — текст
 * заказчицы): строкой под заголовком мастера, а не плашкой на шаге «Программы и сотрудники».
 */
export const PROGRAMS_STEP_HINT =
  'В одной заявке вы можете выбрать все необходимые программы и всех сотрудников, которых нужно сейчас обучить.'
/** Та же подсказка физлицу (individual-training-order): слушатель — он сам. */
export const PROGRAMS_STEP_HINT_INDIVIDUAL =
  'В одной заявке вы можете выбрать все необходимые программы — вы будете их слушателем.'
/**
 * Шаг «Программы и сотрудники» заказа «На заполнении» (training-change-approval; ТЗ п.1.4, В20):
 * напрямую — только слушатели на свободные места, остальное согласует Центрика.
 */
export const NAMING_STEP_HINT =
  'Заявка у менеджера: назовите слушателей на свободные места. Добавить или убрать программу, изменить число мест, убрать или заменить слушателя можно через «Изменить состав» — изменения согласует Центрика.'
/**
 * «На заполнении» у физлица (individual-training-order): изменения состава ему недоступны
 * (training-change-approval) — только через вопрос менеджеру.
 */
export const NAMING_STEP_HINT_INDIVIDUAL =
  'Заявка у менеджера. Чтобы добавить или убрать программу, задайте вопрос менеджеру — кнопка «Задать вопрос» вверху страницы.'
/** Подтверждение данных, пока изменения состава ждут согласования. */
export const CHANGE_PENDING_SUBMIT_NOTICE =
  'Изменения состава на согласовании — подтвердить данные можно после решения по ним.'
/** Шаг «Программы и сотрудники», пока изменения состава ждут согласования. */
export const CHANGE_PENDING_STEP_HINT =
  'Изменения состава на согласовании — состав заявки можно будет менять после решения по ним.'
/**
 * Пометка заказа «На заполнении» (ТЗ п.1.3, текст заказчицы) — на странице мастера, над шагами.
 */
export const REQUEST_SENT_NOTICE =
  'Ваша заявка отправлена менеджеру на просчёт. Вы можете продолжить заполнение заявки либо дождаться ответа менеджера.'
/**
 * Подтверждение данных при местах без названного слушателя (training-listeners-count; ответ
 * заказчицы В15): назвать нужно всех.
 */
export const UNNAMED_PLACES_NOTICE =
  'Укажите всех слушателей: на шаге «Программы и сотрудники» выберите или добавьте сотрудников на каждое место.'

/** Позиция заявки, где названы не все слушатели: ключ, название, сколько названо и сколько мест. */
export interface UnnamedPlace {
  /** `program-<id>` или `package-<id>`. */
  key: string
  label: string
  named: number
  places: number
}

/** Программы и пакеты заказа, где слушателей названо меньше, чем указано (training-listeners-count). */
export function unnamedPlaces(wizard: Pick<TrainingWizard, 'programs' | 'packages'>): UnnamedPlace[] {
  const out: UnnamedPlace[] = []
  for (const p of wizard.packages) {
    if (p.listenersCount > p.selectedPersonIds.length) {
      out.push({
        key: `package-${p.id}`,
        label: `Пакет «${p.name}»`,
        named: p.selectedPersonIds.length,
        places: p.listenersCount,
      })
    }
  }
  for (const p of wizard.programs) {
    if (p.listenersCount > p.selectedPersonIds.length) {
      out.push({
        key: `program-${p.id}`,
        label: p.shortName || p.name,
        named: p.selectedPersonIds.length,
        places: p.listenersCount,
      })
    }
  }
  return out
}
const ALL_FILTER = '__all__'
/**
 * Группа сотрудников без указанного подразделения: в обучении — общая с «Не указано»
 * (training-listeners-count), чтобы рядом не было двух групп «ни о чём».
 */
const NO_DEPARTMENT = UNSPECIFIED_DEPARTMENT

/**
 * Цветные рамки кнопок массового выбора (ответ заказчицы 04.11 к п.1.3: «сейчас они теряются»).
 * Цвет — по смыслу: просроченные — красный, истекающие — янтарный, «требующие внимания» (и те, и
 * другие, и без данных) — янтарный с заливкой, «всех» — акцент, «снять выбор» — нейтральная.
 */
const MASS_ACTION_TONE: Record<TrainingMassActionPreset, string> = {
  all: 'border-accent text-accent-txt hover:bg-accent-bg',
  expired: 'border-red text-red hover:bg-red-bg',
  expiring: 'border-amber text-amber hover:bg-amber-bg',
  attention: 'border-amber bg-amber-bg text-amber hover:bg-amber-bg',
  none: 'border-line-strong text-txt-secondary',
}

// --- Загрузка «Списка обучающихся» (order-import-legacy-forms) ---

/** Заголовок диалога загрузки. */
const TRAINING_IMPORT_TITLE = 'Загрузка списка обучающихся из файла'

/** Подсказка рядом с кнопкой загрузки. */
const TRAINING_IMPORT_HINT =
  'Если слушатели перечислены в Excel — по шаблону кабинета или по форме «Список обучающихся».'

/** Что за файл и что из него попадёт в заказ (training-import-template; ТЗ п.3, В23). */
const TRAINING_IMPORT_DESCRIPTION =
  'Выберите файл Excel по шаблону (программа — из выпадающего списка) или «Список обучающихся»: ' +
  'строка — слушатель и программа. Программа подбирается по коду или названию из каталога, ' +
  'слушатель — по СНИЛС или ФИО с датой рождения среди сотрудников компании. Кого нет в ' +
  'структуре, кабинет добавит в подразделение из файла (или выбранное при загрузке) с должностью ' +
  'из файла; у найденных дозаполнит пустые email, образование, дату рождения, пол и СНИЛС — ' +
  'заполненные в кабинете данные не меняются. Если программа не указана или не подобралась, ' +
  'сотрудник всё равно загрузится — программу выберете здесь, на этом шаге.'

/** Итог загрузки над списком программ. */
function trainingImportNotice(result: OrderImportResult): string {
  const parts: string[] = []
  if (result.added > 0) {
    parts.push(
      `Из файла добавлено слушателей по программам: ${result.added}. Проверьте выбор под программами.`,
    )
  }
  if (result.newEmployees > 0) {
    parts.push(`Новых сотрудников добавлено в структуру: ${result.newEmployees}.`)
  }
  if (result.withoutProgram > 0) {
    parts.push(
      `Строк, где программа не подобрана или для неё нет места: ${result.withoutProgram} — ` +
        'сотрудники загружены, добавьте их в нужные программы на этом шаге.',
    )
  }
  return parts.length > 0
    ? parts.join(' ')
    : 'Новых слушателей в файле не оказалось — все уже есть в заказе.'
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ---------- Шаг 1: данные компании ----------

// Компакт-вид + переключение read-only/форма вынесены в общий `CompanyRequisitesStep`
// (F2-11/FX-E) — переиспользуется здесь и в мастерах СОУТ/профрисков, чтобы паттерн шага-1
// («Изменить» в заголовке блока → форма реквизитов) не расходился между сервисами.
// Режима просмотра у шагов больше нет (MR !36): отправленный заказ показывается экраном
// «Заказ отправлен» и до мастера не доходит, поэтому шаги всегда редактируемые.
function TrainingStepCompany({
  order,
  organization,
  onSaved,
}: {
  order: OrderDetail
  organization?: CompanyProfile
  onSaved: () => void
}) {
  async function handleSave(input: UpdateCompanyProfileInput): Promise<CompanyProfile> {
    await ordersApi.patchRequisites(order.id, input)
    // Возвращаем то, что сохранили — родитель тут же перечитает актуальный снимок мастера.
    return { ...(organization as CompanyProfile), ...input } as CompanyProfile
  }

  return (
    <CompanyRequisitesStep
      organization={organization}
      readOnly={false}
      onSave={handleSave}
      onSaved={onSaved}
      hideManagerFields
      onDaDataLookup={companyApi.dadataLookup}
    />
  )
}

/**
 * Первый шаг физлица (individual-training-order): заказ оформляется на него самого, реквизитов
 * компании нет; данные для документов об обучении — на шаге «Заполнение данных».
 */
function TrainingStepIndividual() {
  return (
    <Card header="Заказ обучения для вас">
      <p className="text-txt">
        Заказ оформляется на вас — вы будете слушателем выбранных программ. На следующем шаге
        выберите программы, затем заполните данные для документов об обучении: СНИЛС, дату рождения,
        образование, e-mail — и загрузите согласие на обработку персональных данных.
      </p>
    </Card>
  )
}

// ---------- Шаг 2: программы и сотрудники ----------

/**
 * Экран диалога выбора сотрудников (centrika-docs,
 * component.create-training-order.programs-and-employees, MR !19): окно содержит ДВА экрана —
 * выбор сотрудников и создание сотрудника, — и одновременно показывает ровно один. Отдельного
 * вложенного модального окна под создание сотрудника нет намеренно (решение РП в MR !19): два
 * модальных слоя дали бы конкурирующие правила закрытия (Escape/клик вне панели).
 */
type EmployeePickerScreen = 'picker' | 'create'

/**
 * Под что выбирают сотрудников: программу, выбранную отдельно, или пакет программ целиком
 * (training-order-packages) — у пакета один список слушателей на все его программы.
 */
export interface EmployeePickerTarget {
  kind: 'program' | 'package'
  id: string
  /** Название в заголовке окна. */
  title: string
  selectedPersonIds: string[]
  /**
   * Число мест позиции (training-listeners-count): выбор сотрудников его не уменьшает, а при
   * выборе большего числа сотрудников оно растёт до их числа.
   */
  listenersCount: number
  /**
   * Пакет или программа, которые выбрать уже нельзя (возвращённый заказ): слушателей только
   * убирают — новых в окне не отметить и не добавить, мест остаётся столько, сколько выбрано (поля
   * числа у них нет).
   */
  shrinkOnly?: boolean
  /**
   * Только слушатели на свободные места (заказ «На заполнении», training-change-approval, В20):
   * уже названных не снять, больше мест не выбрать — остальное через «Изменить состав».
   */
  namingOnly?: boolean
  /**
   * «−» при всех названных местах (ответ заказчицы 04.11 к п.1.4): в окне только выбранные
   * сотрудники, снятые убираются из заказа, число мест уменьшается на их число (не меньше одного).
   */
  removeOnly?: boolean
}

/** Подсказка окна «убрать сотрудников» — текст заказчицы (ответ 04.11 к п.1.4). */
export const REMOVE_LISTENERS_HINT = 'Отмените выбор для сотрудников, которых нужно убрать из заказа'

/**
 * Число мест после выбора в окне: «убрать» — минус снятые (не меньше одного), «только убрать»
 * (позиция вне каталога) — сколько выбрано, иначе — не меньше выбранных.
 */
export function placesAfterPick(target: EmployeePickerTarget, personIds: string[]): number {
  if (target.removeOnly) {
    const removed = target.selectedPersonIds.filter((id) => !personIds.includes(id)).length
    return Math.max(1, target.listenersCount - removed)
  }
  return target.shrinkOnly ? personIds.length : Math.max(target.listenersCount, personIds.length)
}

/** Названного «На заполнении» не снять — это изменение состава (training-change-approval). */
export const NAMING_ONLY_LOCKED = 'Уже назван — убрать или заменить можно через «Изменить состав»'
/** «На заполнении» мест больше, чем заявлено, не выбрать (training-change-approval). */
export const NAMING_ONLY_FULL = 'Свободных мест нет — число мест меняют через «Изменить состав»'

/** Почему в окне нельзя отметить нового сотрудника, когда позицию выбрать уже нельзя. */
const SHRINK_ONLY_BLOCKED: Record<EmployeePickerTarget['kind'], string> = {
  program: 'Программы больше нет в каталоге — новых слушателей не добавить',
  package: 'Пакет больше не доступен — новых слушателей не добавить',
}

interface EmployeePickerState {
  employees: TrainingProgramEmployee[]
  loading: boolean
  error: string | null
  selected: Set<string>
  search: string
  department: string
  position: string
  screen: EmployeePickerScreen
  /**
   * Только что созданный в окне сотрудник (MR !19, п.6): его строка прокручивается в видимую
   * область и подсвечивается до следующего действия в окне (выбор, поиск, фильтр, массовое
   * действие) — иначе в длинном списке новую строку легко не заметить.
   */
  highlightPersonId: string | null
  applying: boolean
}

export function TrainingEmployeePickerModal({
  orderId,
  target,
  onClose,
  onApplied,
  onPick,
  blockedFor,
  loadEmployees,
  allowCreate = true,
}: {
  orderId: string
  target: EmployeePickerTarget
  onClose: () => void
  onApplied?: (wizard: TrainingWizard) => void
  /**
   * Выбор без сохранения (редактор изменений состава, training-change-approval): «Применить» отдаёт
   * выбранных вызывающему, состав заказа не меняется.
   */
  onPick?: (personIds: string[], picked: TrainingProgramEmployee[]) => void
  /**
   * Почему сотрудника нельзя выбрать — по составу, который правит вызывающий (редактор изменений):
   * заменяет причину сервера, посчитанную по нынешнему составу заказа.
   */
  blockedFor?: (personId: string) => string | undefined
  /** Сотрудники для окна — не из своего скоупа клиента (команда, training-change-approval). */
  loadEmployees?: (target: EmployeePickerTarget, signal: AbortSignal) => Promise<TrainingProgramEmployee[]>
  /**
   * «Добавить сотрудника» в окне — сотрудника заводит клиент в своей структуре; команда выбирает
   * из уже заведённых.
   */
  allowCreate?: boolean
}) {
  const [state, setState] = useState<EmployeePickerState>({
    employees: [],
    loading: true,
    error: null,
    selected: new Set(target.selectedPersonIds),
    search: '',
    department: ALL_FILTER,
    position: ALL_FILTER,
    screen: 'picker',
    highlightPersonId: null,
    applying: false,
  })
  const [workplaces, setWorkplaces] = useState<Workplace[]>([])
  // Существующие подразделения — поле «Подразделение» в CreateEmployeeForm (p3-sout-commission
  // §3.1/D4), строгий выбор из существующих записей (не производный список из `workplaces`).
  const [employeeFormDepartments, setEmployeeFormDepartments] = useState<Department[]>([])
  /** Кнопка «Добавить сотрудника» — сюда возвращается фокус при возврате на экран выбора. */
  const addEmployeeButtonRef = useRef<HTMLButtonElement>(null)
  /** Панель экрана создания — принимает фокус при переключении на него. */
  const createScreenRef = useRef<HTMLDivElement>(null)
  /** Скролл-контейнер списка — в нём ищем строку созданного сотрудника, чтобы прокрутить к ней. */
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const controller = new AbortController()
    const load = loadEmployees
      ? loadEmployees(target, controller.signal)
      : target.kind === 'package'
        ? trainingApi.listPackageEmployees(orderId, target.id, controller.signal)
        : trainingApi.listProgramEmployees(orderId, target.id, controller.signal)
    load
      .then((res) => {
        if (controller.signal.aborted) return
        setState((s) => ({ ...s, employees: res, loading: false }))
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setState((s) => ({
            ...s,
            loading: false,
            error: getErrorMessage(err, 'Не удалось загрузить сотрудников'),
          }))
        }
      })
    // Структура клиента — только для формы «Добавить сотрудника».
    if (!allowCreate) return () => controller.abort()
    companyApi
      .listWorkplaces(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setWorkplaces(res.items)
      })
      .catch(() => undefined)
    companyApi
      .listDepartments(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setEmployeeFormDepartments(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, target.kind, target.id])

  const departments = useMemo(
    () => Array.from(new Set(state.employees.map((e) => e.departmentName).filter(Boolean))).sort(),
    [state.employees],
  )
  const positions = useMemo(
    () => Array.from(new Set(state.employees.map((e) => e.position).filter(Boolean))).sort(),
    [state.employees],
  )

  // «На заполнении» (namingOnly): свободных мест — заявлено минус выбрано.
  const freePlaces = target.listenersCount - state.selected.size

  const visible = useMemo(() => {
    const q = state.search.trim().toLowerCase()
    const initiallySelected = new Set(target.selectedPersonIds)
    return state.employees
      // «Убрать сотрудников» — только уже выбранные в позицию.
      .filter((e) => !target.removeOnly || initiallySelected.has(e.personId))
      .map((e) => (blockedFor ? { ...e, blocked: blockedFor(e.personId) } : e))
      .map((e) =>
        // Выбрать уже нельзя — новых слушателей не добавить, причина — под ФИО.
        target.shrinkOnly && !e.blocked && !initiallySelected.has(e.personId)
          ? { ...e, blocked: SHRINK_ONLY_BLOCKED[target.kind] }
          : e,
      )
      .map((e) => {
        if (!target.namingOnly) return e
        if (initiallySelected.has(e.personId)) return { ...e, blocked: NAMING_ONLY_LOCKED }
        return !e.blocked && freePlaces <= 0 && !state.selected.has(e.personId)
          ? { ...e, blocked: NAMING_ONLY_FULL }
          : e
      })
      .filter(
        (e) =>
          (state.department === ALL_FILTER || e.departmentName === state.department) &&
          (state.position === ALL_FILTER || e.position === state.position) &&
          (q === '' || e.fullName.toLowerCase().includes(q)),
      )
      .sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru'))
  }, [state.employees, state.search, state.department, state.position, state.selected, freePlaces, blockedFor, target.shrinkOnly, target.namingOnly, target.removeOnly, target.kind, target.selectedPersonIds])

  /**
   * Группы списка — ПОДРАЗДЕЛЕНИЯ сотрудников (centrika-docs,
   * component.create-training-order.programs-and-employees, MR !18; прежде группами были
   * состояния обучения относительно программы — просрочена/скоро истекает/нет данных/актуальна).
   * Сотрудники без подразделения — в группе «Не указано» после всех остальных; сами
   * группы — по алфавиту, внутри группы порядок по ФИО задан сортировкой `visible` выше.
   * Индивидуальный статус обучения по программе и дата окончания остаются бейджем в строке
   * сотрудника — меняется только принцип группировки.
   */
  const grouped = useMemo(() => {
    const groups = new Map<string, TrainingProgramEmployee[]>()
    for (const e of visible) {
      const department = e.departmentName.trim() || NO_DEPARTMENT
      const items = groups.get(department)
      if (items) items.push(e)
      else groups.set(department, [e])
    }
    // «Не указано» (и сотрудники без подразделения) — после всех остальных.
    const last = (d: string) => d === NO_DEPARTMENT || isUnspecifiedDepartment(d)
    return Array.from(groups.entries())
      .sort(([a], [b]) =>
        last(a) !== last(b) ? (last(a) ? 1 : -1) : a.localeCompare(b, 'ru'),
      )
      .map(([department, items]) => ({ department, items }))
  }, [visible])

  /**
   * Фокус при переключении экранов (MR !19): Radix Dialog расставляет фокус только при открытии
   * самого окна — смену экранов ВНУТРИ уже открытого окна он не видит, поэтому переносим фокус
   * руками. На экран создания — на его панель (`role="group"` + `aria-label`, скринридер
   * объявляет «Новый сотрудник»), обратно — на кнопку «Добавить сотрудника», с которой ушли.
   * Первый рендер не трогаем: там фокус ставит сам Dialog.
   */
  const previousScreen = useRef(state.screen)
  useEffect(() => {
    if (previousScreen.current === state.screen) return
    previousScreen.current = state.screen
    if (state.screen === 'create') createScreenRef.current?.focus()
    else addEmployeeButtonRef.current?.focus()
  }, [state.screen])

  /**
   * Прокрутка к строке созданного сотрудника (MR !19, п.6). `block: 'nearest'` — сдвигаем список
   * ровно настолько, чтобы строка стала видимой, и не дёргаем остальную разметку окна.
   */
  useEffect(() => {
    if (state.screen !== 'picker' || !state.highlightPersonId) return
    const row = listRef.current?.querySelector(`[data-person-id="${state.highlightPersonId}"]`)
    row?.scrollIntoView({ block: 'nearest' })
  }, [state.screen, state.highlightPersonId])

  function toggle(personId: string) {
    setState((s) => {
      const next = new Set(s.selected)
      if (next.has(personId)) next.delete(personId)
      else next.add(personId)
      // Любое действие в окне снимает подсветку созданной строки (MR !19, п.6).
      return { ...s, selected: next, highlightPersonId: null }
    })
  }

  function pickPreset(preset: TrainingMassActionPreset) {
    // Сотрудников, которых здесь выбрать нельзя (программа у них уже в пакете или выбрана
    // отдельно), массовые действия не трогают.
    const selectable = visible.filter((e) => !e.blocked)
    const ids = applyTrainingMassAction(selectable, preset)
    setState((s) => {
      if (preset === 'none') {
        const next = new Set(s.selected)
        selectable.forEach((e) => next.delete(e.personId))
        return { ...s, selected: next, highlightPersonId: null }
      }
      const next = new Set(s.selected)
      ids.forEach((id) => next.add(id))
      return { ...s, selected: next, highlightPersonId: null }
    })
  }

  /**
   * «Вернуться к выбору» (MR !19): экран выбора открывается БЕЗ создания сотрудника. Выбранные
   * сотрудники, поисковый запрос и значения фильтров сохраняются — меняем только `screen`,
   * остальное состояние окна не трогаем (форма создания живёт в собственном состоянии и
   * размонтируется вместе с экраном, введённые в неё данные не сохраняются).
   */
  function backToPicker() {
    setState((s) => ({ ...s, screen: 'picker' }))
  }

  function onEmployeeCreated(employee: Employee) {
    setState((s) => ({
      ...s,
      // MR !19: после создания окно возвращается на экран выбора сотрудников — созданный
      // сотрудник виден в списке, но в программу он попадёт только по кнопке «Применить»
      // (само создание сохраняет его в организации).
      screen: 'picker',
      // MR !19, п.6: строка созданного сотрудника подсвечивается и прокручивается в вид.
      highlightPersonId: employee.id,
      // §6 (D8): активные фильтр/поиск, оставшиеся от предыдущего просмотра списка, могли не
      // совпадать с подразделением/должностью только что созданного сотрудника — `visible`
      // фильтрует `state.employees` по ним ДО рендера строк, и новая строка молча пропадала бы
      // из вида (хотя формально уже выбрана). Сбрасываем их сразу после создания — новый
      // сотрудник гарантированно попадает в видимый список, как того требует критерий приёмки.
      search: '',
      department: ALL_FILTER,
      position: ALL_FILTER,
      employees: [
        ...s.employees,
        {
          personId: employee.id,
          fullName: employee.fullName,
          departmentName: employee.departmentName,
          position: employee.position,
          status: 'in_process',
          selected: true,
        },
      ],
      // «На заполнении» без свободного места созданный сотрудник в списке, но не выбран.
      selected:
        target.namingOnly && target.listenersCount - s.selected.size <= 0
          ? s.selected
          : new Set(s.selected).add(employee.id),
    }))
  }

  async function apply() {
    const personIds = [...state.selected]
    if (onPick) {
      onPick(
        personIds,
        state.employees.filter((e) => state.selected.has(e.personId)),
      )
      onClose()
      return
    }
    setState((s) => ({ ...s, applying: true, error: null }))
    try {
      const places = placesAfterPick(target, personIds)
      const wizard = await trainingApi.patchComposition(
        orderId,
        target.kind === 'package'
          ? { packageId: target.id, personIds, listenersCount: places }
          : { programId: target.id, personIds, listenersCount: places },
      )
      onApplied?.(wizard)
      onClose()
    } catch (err) {
      setState((s) => ({
        ...s,
        applying: false,
        error: getErrorMessage(err, 'Не удалось сохранить выбор сотрудников'),
      }))
    }
  }

  const creating = state.screen === 'create'

  return (
    // Отмена (кнопка «Отмена» ниже) не имеет побочных эффектов — просто закрывает окно без
    // сохранения выбора, поэтому Escape/клик вне панели безопасно заводим на тот же onClose.
    // Оба экрана окна закрываются одинаково — второго модального слоя нет (MR !19).
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      {/* Программный заголовок диалога меняется вместе с экраном (MR !19) — скринридер получает
          актуальное название того, что показано сейчас, а не название окна «вообще». */}
      <DialogContent
        title={
          creating
            ? 'Новый сотрудник'
            : `${target.removeOnly ? 'Убрать сотрудников' : 'Выбор сотрудников'} — ${target.title}`
        }
        className="max-w-none"
      >
        {creating ? (
          /* Экран создания сотрудника (MR !19): ни списка, ни фильтров, ни массовых действий, ни
             кнопок применения выбора — только shared-форма «Добавить нового сотрудника» (её поля
             и валидация не меняются, видимый заголовок «Новый сотрудник» — её собственный) и
             возврат к выбору. Панель принимает фокус при переключении экрана. */
          <Card
            ref={createScreenRef}
            tabIndex={-1}
            role="group"
            aria-label="Новый сотрудник"
            // Панель принимает ПРОГРАММНЫЙ фокус, поэтому кольцо обязано быть видимым (WCAG 2.4.7):
            // конвенция ДС — `focus-visible:ring-2 ring-accent` с offset (см. button.tsx, а также
            // `.ds-focus-ring`). Вариант `focus-visible` (а не всегда видимое кольцо) выбран
            // намеренно: браузер наследует focus-visible от элемента, с которого ушёл фокус, — пришёл
            // клавиатурой с кнопки «Добавить сотрудника» → кольцо есть, кликнул мышью → нет.
            // Голого `outline-none` здесь нет: если браузер focus-visible не выставит, останется
            // системный контур — фокус не может стать невидимым ни в одной из веток.
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-panel"
            footer={
              <Button variant="secondary" onClick={backToPicker}>
                Вернуться к выбору
              </Button>
            }
          >
            <CreateEmployeeForm
              api={companyApi}
              workplaces={workplaces}
              employees={[]}
              departments={employeeFormDepartments}
              onCreated={onEmployeeCreated}
              departmentOptional
            />
          </Card>
        ) : (
          // Заголовок и кнопки (подвал) закреплены, прокручивается только тело с поиском и списком —
          // «Добавить сотрудника», «Применить» и «Отмена» видны без прокрутки (ответ заказчицы
          // 04.11 к п.1.3).
          <Card
            scrollable
            className="max-h-dialog"
            footer={
              <div className="flex flex-wrap items-center gap-2">
                {target.shrinkOnly || target.removeOnly || !allowCreate ? null : (
                  // MR !19: форма создания не разворачивается под списком — окно переключается на
                  // отдельный экран создания сотрудника.
                  <Button
                    ref={addEmployeeButtonRef}
                    variant="secondary"
                    leftIcon={<UserPlus className="size-4 stroke-current" aria-hidden />}
                    onClick={() => setState((s) => ({ ...s, screen: 'create' }))}
                  >
                    Добавить сотрудника
                  </Button>
                )}
                <div className="ml-auto flex gap-2">
                  <Button disabled={state.applying} onClick={() => void apply()}>
                    {state.applying ? 'Сохранение…' : 'Применить'}
                  </Button>
                  <Button variant="secondary" onClick={onClose}>
                    Отмена
                  </Button>
                </div>
              </div>
            }
            header={
              <div className="flex items-center justify-between gap-2">
                <span>
                  {target.removeOnly ? 'Убрать сотрудников' : 'Выбор сотрудников'} — {target.title}
                </span>
                <span className="text-13 font-normal text-txt-secondary">
                  {target.namingOnly
                    ? `Выбрано: ${state.selected.size} из ${target.listenersCount}`
                    : `Выбрано: ${state.selected.size}`}
                </span>
              </div>
            }
          >
            {state.error ? (
              <Alert variant="danger" className="mb-3">
                {state.error}
              </Alert>
            ) : null}

            <div className="mb-3 flex flex-wrap gap-2">
              <div className="min-w-48 flex-1">
                <Input
                  placeholder="Поиск по ФИО"
                  value={state.search}
                  onChange={(e) =>
                    setState((s) => ({ ...s, search: e.target.value, highlightPersonId: null }))
                  }
                  aria-label="Поиск по ФИО"
                />
              </div>
              <Select
                value={state.department}
                onValueChange={(v) =>
                  setState((s) => ({ ...s, department: v, highlightPersonId: null }))
                }
              >
                <SelectTrigger className="w-52" aria-label="Подразделение">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER}>Все подразделения</SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={state.position}
                onValueChange={(v) =>
                  setState((s) => ({ ...s, position: v, highlightPersonId: null }))
                }
              >
                <SelectTrigger className="w-52" aria-label="Должность">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER}>Все должности</SelectItem>
                  {positions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {target.removeOnly ? (
              <Alert variant="info" className="mb-3">
                {REMOVE_LISTENERS_HINT}
              </Alert>
            ) : null}
            {/* «На заполнении» мест — сколько заявлено: массовый выбор их бы превысил; «убрать
                сотрудников» — только снимают отметки. */}
            {target.namingOnly || target.removeOnly ? null : (
              <div className="mb-3 flex flex-wrap gap-2">
                {(Object.keys(TRAINING_MASS_ACTION_LABELS) as TrainingMassActionPreset[]).map(
                  (preset) => (
                    <Button
                      key={preset}
                      size="sm"
                      variant="secondary"
                      className={MASS_ACTION_TONE[preset]}
                      onClick={() => pickPreset(preset)}
                    >
                      {TRAINING_MASS_ACTION_LABELS[preset]}
                    </Button>
                  ),
                )}
              </div>
            )}

            <div ref={listRef} className="rounded-10 border border-line">
              {state.loading ? (
                <p className="p-4 text-txt-secondary">Загрузка…</p>
              ) : grouped.length === 0 ? (
                <p className="p-4 text-txt-secondary">
                  Никого не найдено — измените поиск или фильтры.
                </p>
              ) : (
                grouped.map(({ department, items }) => (
                  <div key={department} data-testid="employee-group">
                    <div
                      data-testid="employee-group-title"
                      className="bg-gray-bg px-3 py-1.5 text-12 font-semibold uppercase text-txt-tertiary"
                    >
                      {department}
                    </div>
                    {items.map((e) => (
                      <label
                        key={e.personId}
                        data-person-id={e.personId}
                        // MR !19, п.6: строка только что созданного сотрудника выделена до
                        // следующего действия в окне — кольцом из токенов (фон уступает hover).
                        data-highlighted={e.personId === state.highlightPersonId ? '' : undefined}
                        className={cn(
                          'flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0',
                          e.blocked ? 'cursor-not-allowed' : 'hover:bg-hover',
                          e.personId === state.highlightPersonId &&
                            'bg-accent-bg ring-2 ring-inset ring-accent',
                        )}
                      >
                        {/* Программа у сотрудника в заказе уже есть — в пакете или отдельно
                            (training-order-packages): здесь его не выбрать, причина — под ФИО. */}
                        <Checkbox
                          checked={state.selected.has(e.personId)}
                          disabled={Boolean(e.blocked)}
                          aria-describedby={e.blocked ? `blocked-${e.personId}` : undefined}
                          onCheckedChange={() => toggle(e.personId)}
                        />
                        <span className="flex-1">
                          <span className="font-medium text-txt">{e.fullName}</span>
                          <span className="block text-12 text-txt-tertiary">
                            {e.position}
                            {e.departmentName ? ` · ${e.departmentName}` : ''}
                          </span>
                          {e.blocked ? (
                            <span
                              id={`blocked-${e.personId}`}
                              className="block text-12 text-txt-secondary"
                            >
                              {e.blocked}
                            </span>
                          ) : null}
                        </span>
                        <Badge status={TRAINING_STATUS_VIEW[e.status].role}>
                          {TRAINING_STATUS_VIEW[e.status].label}
                          {e.dueOn ? ` · до ${formatDate(e.dueOn)}` : ''}
                        </Badge>
                      </label>
                    ))}
                  </div>
                ))
              )}
            </div>

          </Card>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Шаг 2 «Программы и сотрудники» (training-program-picker; ТЗ 26.10.2026, п.4.1–4.6): выбранные
 * программы с слушателями (п.4.5) и выбор программ деревом разделов каталога (п.4.1–4.3, 4.6).
 * Сотрудников под программу выбирают в модалке; в черновике осталась архивная программа — её
 * видно с пометкой, её слушателей можно только убрать. Пакеты программ (training-order-packages) —
 * первым разделом дерева, выбираются целиком с одним списком слушателей; пакет, который выбрать
 * уже нельзя, — так же только убрать.
 */
/**
 * Как правится состав на шаге «Программы и сотрудники» (training-change-approval):
 * - `direct` — сразу на сервере: черновик и возвращённый на доработку заказ;
 * - `naming` — заказ «На заполнении»: только слушатели на свободные места, остальное — «Изменить
 *   состав» (`onProposeChange`);
 * - `readonly` — изменения состава ждут согласования: только просмотр;
 * - `proposal` — редактор изменений: правки копятся у вызывающего (`onPatch`), на сервер уходят
 *   одним предложением.
 */
export type ProgramsStepMode = 'direct' | 'naming' | 'readonly' | 'proposal'

/** Ключ позиции заявки: `program-<id>` или `package-<id>`. */
export function compositionKey(kind: EmployeePickerTarget['kind'], id: string): string {
  return `${kind}-${id}`
}

/** Позиция с выпущенным итоговым документом в редакторе изменений (ТЗ п.1.4). */
export const LOCKED_ITEM_LABEL = 'Итоговый документ выпущен — без изменений'

export function TrainingStepPrograms({
  orderId,
  draft,
  programs,
  packages,
  selectedEmployees,
  onWizardUpdate,
  onImportFile,
  mode = 'direct',
  onPatch,
  onPicked,
  blockedFor,
  locked,
  onProposeChange,
  loadEmployees,
  allowCreate = true,
  lockedPrograms,
  presetPersonId,
  showPrice = false,
  individual = false,
  selectionFooter,
}: {
  orderId: string
  /** Черновик (иначе — возвращённый на доработку заказ). */
  draft: boolean
  programs: TrainingProgramComposition[]
  packages: TrainingPackageComposition[]
  /** Выбранные сотрудники (§5) — ФИО и должность слушателей по `personId`. */
  selectedEmployees: TrainingSelectedEmployee[]
  onWizardUpdate?: (wizard: TrainingWizard) => void
  /** Открыть загрузку «Списка обучающихся» (order-import-legacy-forms); нет — кнопки нет. */
  onImportFile?: () => void
  mode?: ProgramsStepMode
  /** Правка позиции в редакторе изменений (`proposal`): состав у вызывающего. */
  onPatch?: (patch: TrainingCompositionPatch) => void
  /** Сотрудники, выбранные в окне редактора, — их ФИО и должность для списка слушателей. */
  onPicked?: (picked: TrainingProgramEmployee[]) => void
  /** Почему сотрудника нельзя выбрать в позицию — по составу редактора. */
  blockedFor?: (target: EmployeePickerTarget, personId: string) => string | undefined
  /** Позиции с выпущенным итоговым документом (`compositionKey`) — без изменений. */
  locked?: ReadonlySet<string>
  /** «Изменить состав» (`naming`): открыть редактор изменений; нет — кнопки нет. */
  onProposeChange?: () => void
  /** Сотрудники для окна выбора не из скоупа клиента (команда) — см. `TrainingEmployeePickerModal`. */
  loadEmployees?: (target: EmployeePickerTarget, signal: AbortSignal) => Promise<TrainingProgramEmployee[]>
  /** «Добавить сотрудника» в окне выбора (у команды — нет). */
  allowCreate?: boolean
  /**
   * Программы с выпущенным итоговым документом (ТЗ п.1.4): в редакторе изменений их не добавить —
   * ни отдельно, ни в составе пакета (их слушателей не изменить).
   */
  lockedPrograms?: ReadonlySet<string>
  /** Сотрудник, который сразу добавляется в программу или пакет, добавленные из каталога. */
  presetPersonId?: string
  /** Цена программ справа — команде в редакторе изменений (цену сервер отдаёт только команде). */
  showPrice?: boolean
  /**
   * Физлицо (individual-training-order): слушатель всегда один — он сам, поэтому ни числа мест,
   * ни выбора сотрудников; позицию можно только убрать.
   */
  individual?: boolean
  /**
   * Под «Выбранными программами», закреплено вместе с ними — в редакторе изменений «Что изменится»
   * и кнопки «Отправить на согласование» / «Отмена» (ответ заказчицы 04.11 к п.4.1–4.3).
   */
  selectionFooter?: ReactNode
}) {
  const [picker, setPicker] = useState<EmployeePickerTarget | null>(null)
  // Архивная программа или недоступный пакет, оставшиеся в черновике (training-catalog-v2,
  // training-order-packages): их слушателей можно только убрать — добавить в заказ их нельзя.
  // Позиция, которую сейчас сохраняют. Пока идёт любое сохранение, правка состава недоступна:
  // ответ — мастер целиком, и параллельный ответ другой позиции мог бы его перезаписать.
  const [removingId, setRemovingId] = useState<string | null>(null)
  const saving = removingId !== null
  const [removeError, setRemoveError] = useState<string | null>(null)
  // Дерево разделов каталога — общий справочник; без него программы показываются одним списком.
  const [directions, setDirections] = useState<TrainingDirectionItem[]>([])

  useEffect(() => {
    const controller = new AbortController()
    trainingApi
      .listDirections(controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setDirections(d)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  /** Правка позиции заявки одним PATCH: добавить, сменить число слушателей, убрать. */
  async function save(key: string, patch: TrainingCompositionPatch, failure: string) {
    if (mode === 'proposal') {
      onPatch?.(patch)
      return
    }
    setRemoveError(null)
    setRemovingId(key)
    try {
      const wizard = await trainingApi.patchComposition(orderId, patch)
      onWizardUpdate?.(wizard)
    } catch (err) {
      setRemoveError(getErrorMessage(err, failure))
    } finally {
      setRemovingId(null)
    }
  }

  if (programs.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Каталог программ пуст"
        description="Обратитесь к менеджеру Центрики — программы обучения ещё не заведены"
      />
    )
  }

  const selectable = programs.filter((p) => !p.archived)
  const selectablePackages = packages.filter((p) => p.available)
  // Состав правится целиком (добавить, убрать, число мест): черновик, возвращённый заказ, редактор.
  const editable = mode === 'direct' || mode === 'proposal'
  const isLocked = (kind: EmployeePickerTarget['kind'], id: string) =>
    locked?.has(compositionKey(kind, id)) ?? false

  function removeButton(key: string, patch: TrainingCompositionPatch, failure: string, label: string) {
    return (
      <Button
        size="sm"
        variant="secondary"
        aria-label={`Убрать из заказа — ${label}`}
        disabled={saving}
        onClick={() => void save(key, patch, failure)}
      >
        {removingId === key ? 'Удаление…' : 'Убрать из заказа'}
      </Button>
    )
  }

  function pickButton(label: string, selected: number, target: EmployeePickerTarget) {
    return (
      <Button
        size="sm"
        variant="secondary"
        aria-label={`${selected > 0 ? 'Изменить выбор' : 'Выбрать сотрудников'} — ${label}`}
        disabled={saving}
        onClick={() => setPicker(target)}
      >
        {selected > 0 ? 'Изменить выбор' : 'Выбрать сотрудников'}
      </Button>
    )
  }

  /** «Добавить» в каталоге: позиция с одним слушателем, сотрудников можно не называть (ТЗ п.1.3). */
  function addButton(key: string, patch: TrainingCompositionPatch, label: string) {
    return (
      <Button
        size="sm"
        aria-label={`Добавить в заказ — ${label}`}
        disabled={saving}
        onClick={() => void save(key, patch, 'Не удалось добавить в заказ')}
      >
        {removingId === key ? 'Сохранение…' : 'Добавить'}
      </Button>
    )
  }

  const programLabel = (p: TrainingProgramComposition) => p.shortName || p.name
  const packageLabel = (p: TrainingPackageComposition) => `пакет «${p.name}»`

  function programPatch(p: TrainingProgramComposition, listenersCount: number, personIds = p.selectedPersonIds) {
    return { programId: p.id, personIds, listenersCount }
  }
  function packagePatch(p: TrainingPackageComposition, listenersCount: number, personIds = p.selectedPersonIds) {
    return { packageId: p.id, personIds, listenersCount }
  }

  /**
   * «На заполнении» — только слушатели на свободные места (выбор без снятия и сверх мест); когда
   * все места названы, действий у позиции нет.
   */
  function namingAction(label: string, target: EmployeePickerTarget) {
    if (target.selectedPersonIds.length >= target.listenersCount || target.shrinkOnly) return null
    return (
      <Button
        size="sm"
        variant="secondary"
        aria-label={`Выбрать сотрудников — ${label}`}
        onClick={() => setPicker({ ...target, namingOnly: true })}
      >
        Выбрать сотрудников
      </Button>
    )
  }

  const lockedBadge = <Badge status="neutral">{LOCKED_ITEM_LABEL}</Badge>

  /** Действия выбранной программы: выбор сотрудников и «Убрать из заказа». */
  function actions(p: TrainingProgramComposition) {
    const label = programLabel(p)
    const target: EmployeePickerTarget = {
      kind: 'program',
      id: p.id,
      title: label,
      selectedPersonIds: p.selectedPersonIds,
      listenersCount: p.listenersCount,
      shrinkOnly: p.archived,
    }
    if (mode === 'readonly') return null
    if (mode === 'naming') return namingAction(label, target)
    if (isLocked('program', p.id)) return lockedBadge
    const remove = removeButton(p.id, programPatch(p, 0, []), 'Не удалось убрать программу из заказа', label)
    // Программа прежнего каталога в черновике — только убрать целиком; у возвращённого на доработку
    // заказа её слушателей можно убирать по одному (новых окно выбора не даст), как у пакета.
    if (p.archived && draft) return remove
    if (individual && p.selectedPersonIds.length > 0) return remove
    return (
      <>
        {pickButton(label, p.selectedPersonIds.length, target)}
        {remove}
      </>
    )
  }

  /** Число слушателей выбранной программы; у программы, которой нет в каталоге, — только бейдж. */
  function programCount(p: TrainingProgramComposition) {
    if (individual || p.archived || !editable || isLocked('program', p.id)) return undefined
    return (
      <ListenersCountInput
        label={programLabel(p)}
        value={p.listenersCount}
        min={Math.max(1, p.selectedPersonIds.length)}
        disabled={saving}
        onChange={(next) => void save(p.id, programPatch(p, next), 'Не удалось изменить число слушателей')}
        onRemoveListeners={
          p.selectedPersonIds.length > 0
            ? () =>
                setPicker({
                  kind: 'program',
                  id: p.id,
                  title: programLabel(p),
                  selectedPersonIds: p.selectedPersonIds,
                  listenersCount: p.listenersCount,
                  removeOnly: true,
                })
            : undefined
        }
      />
    )
  }

  function packageActions(p: TrainingPackageComposition) {
    const key = `package-${p.id}`
    const label = packageLabel(p)
    const target: EmployeePickerTarget = {
      kind: 'package',
      id: p.id,
      title: label,
      selectedPersonIds: p.selectedPersonIds,
      listenersCount: p.listenersCount,
      shrinkOnly: !p.available,
    }
    if (mode === 'readonly') return null
    if (mode === 'naming') return namingAction(label, target)
    if (isLocked('package', p.id)) return lockedBadge
    const remove = removeButton(key, packagePatch(p, 0, []), 'Не удалось убрать пакет из заказа', label)
    // Недоступный пакет черновика — только убрать целиком; у возвращённого на доработку заказа его
    // слушателей можно убирать по одному (новых окно выбора не даст).
    if (!p.available && draft) return remove
    if (individual && p.selectedPersonIds.length > 0) return remove
    return (
      <>
        {pickButton(label, p.selectedPersonIds.length, target)}
        {remove}
      </>
    )
  }

  /** Число слушателей пакета; у пакета, который выбрать уже нельзя, — только бейдж. */
  function packageCount(p: TrainingPackageComposition) {
    if (individual || !p.available || !editable || isLocked('package', p.id)) return undefined
    const key = `package-${p.id}`
    return (
      <ListenersCountInput
        label={packageLabel(p)}
        value={p.listenersCount}
        min={Math.max(1, p.selectedPersonIds.length)}
        disabled={saving}
        onChange={(next) => void save(key, packagePatch(p, next), 'Не удалось изменить число слушателей')}
        onRemoveListeners={
          p.selectedPersonIds.length > 0
            ? () =>
                setPicker({
                  kind: 'package',
                  id: p.id,
                  title: packageLabel(p),
                  selectedPersonIds: p.selectedPersonIds,
                  listenersCount: p.listenersCount,
                  removeOnly: true,
                })
            : undefined
        }
      />
    )
  }

  /** Каталог: выбранное — число слушателей (правится в «Выбранных программах»), иначе «Добавить». */
  function catalogProgramActions(p: TrainingProgramComposition) {
    if (p.listenersCount > 0) return <Badge status="info">{listenersCount(p.listenersCount)}</Badge>
    if (lockedPrograms?.has(p.id)) return lockedBadge
    return addButton(p.id, programPatch(p, 1, presetPersonId ? [presetPersonId] : []), programLabel(p))
  }
  function catalogPackageActions(p: TrainingPackageComposition) {
    if (p.listenersCount > 0) return <Badge status="info">{listenersCount(p.listenersCount)}</Badge>
    if (p.programs.some((x) => lockedPrograms?.has(x.id))) return lockedBadge
    return addButton(
      `package-${p.id}`,
      packagePatch(p, 1, presetPersonId ? [presetPersonId] : []),
      packageLabel(p),
    )
  }

  // Подсказка выбора программ — под заголовком мастера (`PROGRAMS_STEP_HINT`), плашкой здесь
  // только то, что меняет правила шага: «На заполнении» и изменения на согласовании.
  const hint =
    mode === 'naming'
      ? individual
        ? NAMING_STEP_HINT_INDIVIDUAL
        : NAMING_STEP_HINT
      : mode === 'readonly'
        ? CHANGE_PENDING_STEP_HINT
        : null

  return (
    <div className="flex flex-col gap-4">
      {hint ? <Alert variant="info">{hint}</Alert> : null}
      {removeError ? <Alert variant="danger">{removeError}</Alert> : null}
      {mode === 'naming' && onProposeChange ? (
        <div>
          <Button variant="secondary" onClick={onProposeChange}>
            Изменить состав
          </Button>
        </div>
      ) : null}
      {/* Загрузка «Списка обучающихся» (order-import-legacy-forms): программы и слушатели из Excel.
          «На заполнении» — только слушатели на свободные места (проверяет сервер). */}
      {onImportFile && (mode === 'direct' || mode === 'naming') ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={onImportFile}
            leftIcon={<FileSpreadsheet className="size-4 stroke-current" aria-hidden />}
          >
            {ORDER_IMPORT_BUTTON}
          </Button>
          <Button
            variant="secondary"
            onClick={() => downloadFromUrl(trainingApi.importTemplateUrl())}
            leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
          >
            {ORDER_IMPORT_TEMPLATE_BUTTON}
          </Button>
          <span className="text-13 text-txt-secondary">{TRAINING_IMPORT_HINT}</span>
        </div>
      ) : null}

      <SelectedPrograms
        programs={programs}
        packages={packages}
        employees={selectedEmployees}
        renderActions={actions}
        renderPackageActions={packageActions}
        renderCount={programCount}
        renderPackageCount={packageCount}
        draft={draft}
        filling={mode === 'naming' || mode === 'readonly'}
        showPrice={showPrice}
        // Закреплён, пока ниже листается каталог (ответ заказчицы 04.11 к п.4.1–4.3).
        pinned={editable}
        footer={selectionFooter}
      />

      {/* Добавить программу или пакет — там, где состав правится целиком. */}
      {editable ? (
        <section aria-label="Каталог программ">
          <h3 className="mb-2 text-15 font-semibold text-txt">Каталог программ</h3>
          {/* Архивная программа и недоступный пакет в черновике — только в «Выбранных программах»
              (с «Убрать из заказа»), в каталоге их нет. */}
          <ProgramTreePicker
            programs={selectable}
            directions={directions}
            selectedCount={(p) => p.listenersCount}
            renderActions={catalogProgramActions}
            packages={selectablePackages}
            packageSelectedCount={(p) => p.listenersCount}
            renderPackageActions={catalogPackageActions}
            showPrice={showPrice}
          />
        </section>
      ) : null}

      {picker ? (
        <TrainingEmployeePickerModal
          orderId={orderId}
          target={picker}
          onClose={() => setPicker(null)}
          onApplied={onWizardUpdate}
          onPick={
            mode === 'proposal'
              ? (personIds, picked) => {
                  onPicked?.(picked)
                  const listeners = placesAfterPick(picker, personIds)
                  onPatch?.(
                    picker.kind === 'package'
                      ? { packageId: picker.id, personIds, listenersCount: listeners }
                      : { programId: picker.id, personIds, listenersCount: listeners },
                  )
                }
              : undefined
          }
          blockedFor={blockedFor ? (personId) => blockedFor(picker, personId) : undefined}
          loadEmployees={loadEmployees}
          allowCreate={allowCreate}
        />
      ) : null}
    </div>
  )
}

// ---------- Шаг 4: подтверждение ----------

/**
 * Шаг «Подтверждение» (ТЗ 26.10.2026, п.1.1; training-listener-completeness): сводка заказа — состав
 * по программам и пакетам (вид п.4.5) и «Заполнено N из M». Данные слушателей заполняют на шаге
 * «Заполнение данных»; кнопка подтверждения — в панели мастера, активна, когда заполнены все.
 */
function TrainingStepConfirm({
  draft,
  filling = false,
  programs,
  packages,
  employees,
  complete,
  total,
}: {
  /** Состав открыт (черновик, «На заполнении»); иначе — возвращённый на доработку заказ. */
  draft: boolean
  /** Заказ «На заполнении» — пометки недоступного с «Изменить состав». */
  filling?: boolean
  programs: TrainingProgramComposition[]
  packages: TrainingPackageComposition[]
  employees: TrainingSelectedEmployee[]
  /** Слушатели со всеми данными. */
  complete: number
  /** Все слушатели и места без названного слушателя. */
  total: number
}) {
  if (total === 0) {
    return (
      <EmptyState
        icon={Users}
        title="Сотрудники не выбраны"
        description="Вернитесь на шаг 2 и выберите программы и сотрудников"
      />
    )
  }
  return (
    <div className="flex flex-col gap-3">
      <SelectedPrograms
        programs={programs}
        packages={packages}
        employees={employees}
        title="Состав заказа"
        draft={draft}
        filling={filling}
      />
      <p className={cn('text-13', complete === total ? 'text-green' : 'text-txt-secondary')}>
        Данные слушателей: заполнено {complete} из {total}
      </p>
    </div>
  )
}

// ---------- Экран «Заказ отправлен» ----------

/** Тексты экрана отправленного заказа по статусу (training-review-3). */
const SUBMITTED_SCREEN = {
  review: {
    title: 'Заказ отправлен на проверку',
    text: 'Менеджер Центрики проверит данные слушателей и примет заказ в работу.',
  },
  in_progress: {
    title: 'Заказ в работе',
    text: 'Центрика проводит обучение. Ход работ и итоговые документы — на странице заказа.',
  },
  done: {
    title: 'Заказ выполнен',
    text: 'Обучение завершено. Итоговые документы — на странице заказа.',
  },
} as const

function submittedKey(status: OrderDataStatus): keyof typeof SUBMITTED_SCREEN {
  return status === 'in_progress' || status === 'done' ? status : 'review'
}

/**
 * Экран, заменяющий мастер после отправки заказа (MR !36): карточка подтверждения + shared-
 * компонент «Согласия на обработку персональных данных» (centrika-docs!40 — тот же блок
 * отображается на странице заказа клиента). Показывается сразу после успешной отправки И при
 * повторном открытии уже отправленного заказа — прежнего «режима просмотра» мастера больше нет.
 * Отменённый заказ (order-cancel-restore) — «Заказ отменён»: подробности и «Восстановить» — на
 * странице заказа.
 */
function TrainingSubmittedScreen({
  orderId,
  cancelled,
  status,
  employees,
  onEmployeeChanged,
  individual,
}: {
  orderId: string
  cancelled: boolean
  /** Статус заказа: текст экрана — по нему («На проверке», «В работе», «Готово»). */
  status: OrderDataStatus
  employees: TrainingSelectedEmployee[]
  onEmployeeChanged: (employee: TrainingSelectedEmployee) => void
  /** Физлицо (individual-training-order): «Ваши документы». */
  individual: boolean
}) {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-4">
      {/* Высота — по содержимому, немного воздуха сверху и снизу (ответ заказчицы 04.11, п.12.1). */}
      <Card>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          {cancelled ? (
            <XCircle className="size-8 shrink-0 stroke-current text-txt-tertiary" aria-hidden />
          ) : (
            <CheckCircle2 className="size-8 shrink-0 stroke-current text-green" aria-hidden />
          )}
          <div className="min-w-0 flex-1 basis-64">
            <h2 className="text-16 font-semibold text-txt">
              {cancelled ? 'Заказ отменён' : SUBMITTED_SCREEN[submittedKey(status)].title}
            </h2>
            <p className="text-13 text-txt-secondary">
              {cancelled
                ? 'Кем, когда и почему отменён заказ — на странице заказа.'
                : SUBMITTED_SCREEN[submittedKey(status)].text}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate(`/orders/${orderId}`)}>Открыть заказ</Button>
            <Button variant="secondary" onClick={() => navigate('/training')}>
              К журналу сроков
            </Button>
          </div>
        </div>
      </Card>

      <EmployeeDocuments
        employees={employees}
        onEmployeeChanged={onEmployeeChanged}
        individual={individual}
      />
    </div>
  )
}

// ---------- Мастер целиком ----------

export interface TrainingRequestProps {
  order: OrderDetail
  wizard: TrainingWizard
  /**
   * Вызывается после успешной отправки заказа — странице это нужно, чтобы убрать текст под
   * заголовком (в состоянии «Заказ отправлен» он не отображается). Состояние «заказ уже был
   * отправлен раньше» страница определяет сама по `wizard.canEdit`.
   */
  onSubmitted?: () => void
  /**
   * Заказ изменился (training-order-filling): заявка отправлена — заказ «На заполнении», либо сервер
   * отклонил действие (409), потому что заказ или абонентское обслуживание сменились, пока мастер
   * был открыт. Странице нужен обновлённый заказ (статус, пометка о заявке, договор и счёт).
   */
  onOrderChanged?: (order: OrderDetail) => void
  /**
   * Текущий шаг — странице, которая показывает шаги в своей шапке (`TrainingSteps`). Без него
   * мастер показывает шаги сам, отдельной карточкой.
   */
  onStepChange?: (step: number) => void
  /**
   * Мастер перечитан (после 409 или отправки) — странице, которая по нему строит шапку (подпись,
   * экран «Заказ отправлен»): иначе она показывала бы состояние первой загрузки.
   */
  onWizardChange?: (wizard: TrainingWizard) => void
  /**
   * «Изменить состав» заказа «На заполнении» (training-change-approval): редактор изменений
   * показывает страница. Нет — кнопки нет.
   */
  onProposeChange?: () => void
  /** Шаг, с которого открыть мастер (после перечитки страницей), иначе — по статусу заказа. */
  initialStep?: number
  /**
   * Сотрудник, который добавляется в каждую программу, добавленную из каталога («Обучить
   * сотрудника» в карточке, training-order-entry-points). Только черновик.
   */
  presetPersonId?: string
}

export function TrainingRequest({
  order,
  wizard: initialWizard,
  onSubmitted,
  onOrderChanged,
  onStepChange,
  onWizardChange,
  onProposeChange,
  initialStep,
  presetPersonId,
}: TrainingRequestProps) {
  const [wizard, setWizard] = useState<TrainingWizard>(initialWizard)
  // Физлицо (individual-training-order): сервер отдаёт «он сам» — единственного слушателя.
  const individual = Boolean(wizard.selfPersonId)
  const stepLabels = trainingStepLabels(individual)
  const [step, setStep] = useState(() => initialStep ?? initialTrainingStep(order))
  const [submitting, setSubmitting] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [justSubmitted, setJustSubmitted] = useState(false)
  // Сколько полей слушателей сейчас сохраняется (шаг «Заполнение данных» сохраняет каждое поле
  // сразу). Заявка и подтверждение сначала дожидаются этих сохранений: кнопку не блокируем — уход
  // с поля (сохранение) и нажатие кнопки идут одним жестом, и заблокированная на этот миг кнопка
  // теряла бы клик.
  const savingFields = useRef(0)
  const savesDone = useRef<Array<() => void>>([])
  const onSaving = useCallback((delta: number) => {
    savingFields.current += delta
    if (savingFields.current === 0) {
      const waiting = savesDone.current
      savesDone.current = []
      waiting.forEach((resolve) => resolve())
    }
  }, [])
  function waitForSaves(): Promise<void> {
    if (savingFields.current === 0) return Promise.resolve()
    return new Promise((resolve) => savesDone.current.push(resolve))
  }
  // Карточки слушателей с некорректным несохранённым значением (СНИЛС, e-mail): пока они есть, с
  // шага «Заполнение данных» не уйти — иначе введённое молча пропало бы, а светофор показывал бы
  // прежнее значение.
  const [invalidDrafts, setInvalidDrafts] = useState<ReadonlySet<string>>(() => new Set())
  const [stepError, setStepError] = useState<string | null>(null)
  // Окно выбора сотрудников на место без названного слушателя (шаг «Заполнение данных»).
  const [placePicker, setPlacePicker] = useState<EmployeePickerTarget | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importDepartments, setImportDepartments] = useState<Department[]>([])
  const [importNotice, setImportNotice] = useState<string | null>(null)

  useEffect(() => {
    onStepChange?.(step)
  }, [step, onStepChange])

  useEffect(() => {
    onWizardChange?.(wizard)
  }, [wizard, onWizardChange])

  /**
   * Экран «Заказ отправлен» вместо мастера (MR !36) — в двух случаях сразу: заказ только что
   * отправлен и заказ был отправлен раньше (`canEdit:false` — статусы «На проверке», «В
   * работе», «Готово»). «Черновик», «На заполнении» и «Требуются правки» — фаза данных, там мастер.
   * Собственный флаг нужен потому, что перечитка мастера после отправки может не удаться
   * (сеть) — экран отправленного заказа от этого зависеть не должен.
   */
  const showSubmitted = justSubmitted || !wizard.canEdit

  function reloadWizard() {
    trainingApi
      .getWizard(order.id)
      .then(setWizard)
      .catch(() => undefined)
  }

  /**
   * Сервер отклонил заявку или отправку (409): заказ или абонентское обслуживание клиента сменились,
   * пока мастер был открыт (заявку уже отправили в другой вкладке, договор обслуживания начался или
   * закончился). Перечитываем заказ и мастер — кнопки мастера приходят в соответствие с сервером.
   */
  function reloadOrderAndWizard() {
    Promise.all([ordersApi.get(order.id), trainingApi.getWizard(order.id)])
      .then(([fresh, wiz]) => {
        setWizard(wiz)
        onOrderChanged?.(fresh)
      })
      .catch(() => undefined)
  }

  // Загрузка «Списка обучающихся» (order-import-legacy-forms). Подразделения структуры нужны для
  // выбора подразделения сотрудников, которых в структуре ещё нет; список грузится при открытии.
  function openImport() {
    setImportNotice(null)
    setImportOpen(true)
    companyApi
      .listDepartments()
      .then((res) => setImportDepartments(res.items))
      .catch(() => setImportDepartments([]))
  }

  function updateEmployee(updated: TrainingSelectedEmployee) {
    patchEmployee(updated.personId, updated)
  }

  /**
   * Сохранённые поля слушателя — поверх текущего состояния мастера: поля карточки сохраняются
   * независимо и могут завершиться в любом порядке, поэтому сливаем только изменённое, а не
   * подменяем всю запись снимком на момент начала сохранения.
   */
  const patchEmployee = useCallback(
    (personId: string, patch: Partial<TrainingSelectedEmployee>) => {
      setWizard((w) => ({
        ...w,
        selectedEmployees: w.selectedEmployees.map((e) =>
          e.personId === personId ? { ...e, ...patch } : e,
        ),
      }))
    },
    [],
  )

  // Стабильная ссылка: карточки слушателей сообщают о некорректных полях из эффекта.
  const setInvalidDraft = useCallback((personId: string, invalid: boolean) => {
    setInvalidDrafts((prev) => {
      if (prev.has(personId) === invalid) return prev
      const next = new Set(prev)
      if (invalid) next.add(personId)
      else next.delete(personId)
      return next
    })
  }, [])

  /** Переход между шагами: с «Заполнения данных» — только без некорректных несохранённых полей. */
  function goToStep(next: number) {
    if (step === DATA_STEP && invalidDrafts.size > 0) {
      setStepError(INVALID_DRAFTS_NOTICE)
      return
    }
    setStepError(null)
    setStep(Math.max(0, Math.min(stepLabels.length - 1, next)))
  }

  // Состав ещё открыт — черновик или «На заполнении» (training-order-filling): программы и пакеты
  // в нём следуют каталогу, как в черновике.
  const composing = order.dataStatus === 'draft' || order.dataStatus === 'filling'
  // Заявка менеджеру ещё не отправлена: черновик не по абонентскому обслуживанию. Подтвердить
  // данные можно только после заявки — до неё главная кнопка мастера «Отправить заявку».
  const requestPending = order.dataStatus === 'draft' && !wizard.subscription
  // Черновик или заказ «На заполнении» с программой, которой больше нет в каталоге, или с пакетом,
  // который выбрать уже нельзя, не отправляется — их убирают на шаге 2 (сервер проверяет то же).
  // Возвращённый на доработку заказ эта проверка не трогает: его состав уже принят командой.
  const unavailableSelected =
    composing &&
    (wizard.programs.some((p) => p.archived && p.listenersCount > 0) ||
      wizard.packages.some((p) => !p.available && p.listenersCount > 0))
  // Места без названного слушателя (training-listeners-count): заявку с ними отправить можно,
  // подтвердить данные — нет, назвать нужно всех (ответ заказчицы В15).
  const unnamed = unnamedPlaces(wizard)
  // Слушатели, у которых заполнено не всё (training-listener-completeness), — в порядке шага
  // «Заполнение данных»: «Осталось заполнить» ведёт к первому из них.
  const incomplete = wizard.selectedEmployees.filter((e) => listenerStatus(e) !== 'complete')
  const leftToFill = incomplete.length + unnamed.reduce((n, u) => n + u.places - u.named, 0)
  const listenersTotal =
    wizard.selectedEmployees.length + unnamed.reduce((n, u) => n + u.places - u.named, 0)
  // Изменения состава на согласовании (training-change-approval): состав заморожен, данные
  // подтверждаются после решения по ним.
  const changePending = Boolean(order.trainingChange)
  // «На заполнении» напрямую — только слушатели на свободные места (В20).
  const programsMode: ProgramsStepMode =
    order.dataStatus === 'filling' ? (changePending ? 'readonly' : 'naming') : 'direct'
  const canSubmit =
    !changePending && !unavailableSelected && wizard.selectedEmployees.length > 0 && leftToFill === 0
  const selectedProgramsCount = wizard.programs.filter((p) => p.listenersCount > 0).length
  const selectedPackagesCount = wizard.packages.filter((p) => p.listenersCount > 0).length
  const canRequest = !unavailableSelected && selectedProgramsCount + selectedPackagesCount > 0

  /** Цель окна выбора по позиции заявки (ключ — `program-<id>` или `package-<id>`). */
  function placeTarget(key: string): EmployeePickerTarget | null {
    const [kind, id] = key.split(/-(.+)/)
    // «На заполнении» — только на свободные места (training-change-approval).
    const namingOnly = order.dataStatus === 'filling'
    if (kind === 'package') {
      const p = wizard.packages.find((x) => x.id === id)
      return p
        ? {
            kind: 'package',
            id: p.id,
            title: `пакет «${p.name}»`,
            selectedPersonIds: p.selectedPersonIds,
            listenersCount: p.listenersCount,
            shrinkOnly: !p.available,
            namingOnly,
          }
        : null
    }
    const p = wizard.programs.find((x) => x.id === id)
    return p
      ? {
          kind: 'program',
          id: p.id,
          title: p.shortName || p.name,
          selectedPersonIds: p.selectedPersonIds,
          listenersCount: p.listenersCount,
          shrinkOnly: p.archived,
          namingOnly,
        }
      : null
  }

  /** «Осталось заполнить»: к шагу «Заполнение данных» и к первой незаполненной строке. */
  function goToFirstIncomplete() {
    setStep(DATA_STEP)
    const first = incomplete[0]
    requestAnimationFrame(() => {
      const el = first ? document.getElementById(listenerRowId(first.personId)) : null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  /**
   * «Отправить заявку» (training-order-filling, ТЗ п.1.3): заказ уходит менеджеру на просчёт —
   * «На заполнении»; данные слушателей клиент довносит после (они и так сохраняются сразу).
   */
  async function sendRequest() {
    setSubmitError(null)
    setFieldErrors([])
    if (!canRequest) {
      setSubmitError(
        unavailableSelected
          ? UNAVAILABLE_SELECTION_NOTICE
          : 'Добавьте в заказ хотя бы одну программу.',
      )
      return
    }
    setRequesting(true)
    try {
      await waitForSaves()
      const updated = await ordersApi.request(order.id)
      onOrderChanged?.(updated)
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setSubmitError('Сервер отклонил заявку — проверьте состав.')
        setFieldErrors((err.details ?? []).map((d) => d.message))
      } else if (err instanceof ApiError && err.status === 409) {
        setSubmitError(err.message)
        reloadOrderAndWizard()
      } else {
        setSubmitError(getErrorMessage(err, 'Не удалось отправить заявку'))
      }
    } finally {
      setRequesting(false)
    }
  }

  async function submit() {
    setSubmitError(null)
    setFieldErrors([])
    if (!canSubmit) {
      setSubmitError(
        changePending
          ? CHANGE_PENDING_SUBMIT_NOTICE
          : unnamed.length > 0
            ? UNNAMED_PLACES_NOTICE
            : LISTENERS_INCOMPLETE_NOTICE,
      )
      return
    }
    setSubmitting(true)
    // Данные слушателей уже в карточках (сохраняются сразу) — подача замораживает «фотографию».
    try {
      await waitForSaves()
      await ordersApi.submit(order.id)
      setJustSubmitted(true)
      onSubmitted?.()
      // Экран «Заказ отправлен» обязан показывать состав на момент отправки (требование),
      // поэтому перечитываем мастер. Не блокируем показ экрана: он уже переключён флагом выше.
      reloadWizard()
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        setSubmitError('Сервер отклонил заказ — проверьте данные слушателей.')
        setFieldErrors((err.details ?? []).map((d) => d.message))
      } else if (err instanceof ApiError && err.status === 409) {
        setSubmitError(err.message)
        reloadOrderAndWizard()
      } else {
        setSubmitError(getErrorMessage(err, 'Не удалось отправить заказ'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (showSubmitted) {
    return (
      <TrainingSubmittedScreen
        orderId={order.id}
        cancelled={order.dataStatus === 'cancelled'}
        status={order.dataStatus}
        employees={wizard.selectedEmployees}
        onEmployeeChanged={updateEmployee}
        individual={individual}
      />
    )
  }

  const lastStep = step === stepLabels.length - 1

  return (
    <div className="flex flex-col gap-4">
      {onStepChange ? null : (
        // Без шапки страницы (мастер сам по себе) шаги — отдельной карточкой.
        <Card>
          <Stepper steps={stepLabels} current={step} />
        </Card>
      )}

      {step === 1 && importNotice ? <Alert variant="info">{importNotice}</Alert> : null}
      {importOpen ? (
        <OrderImportDialog
          title={TRAINING_IMPORT_TITLE}
          description={TRAINING_IMPORT_DESCRIPTION}
          departments={importDepartments}
          templateUrl={trainingApi.importTemplateUrl()}
          preview={(file) => trainingApi.importPreview(order.id, file)}
          importFile={(file, departmentName) =>
            trainingApi.importFile(order.id, file, departmentName)
          }
          onImported={(result) => {
            setImportNotice(trainingImportNotice(result))
            reloadWizard()
          }}
          onClose={() => setImportOpen(false)}
        />
      ) : null}

      {step === 0 ? (
        individual ? (
          <TrainingStepIndividual />
        ) : (
          <TrainingStepCompany
            order={order}
            organization={wizard.organization}
            onSaved={reloadWizard}
          />
        )
      ) : step === 1 ? (
        <TrainingStepPrograms
          orderId={order.id}
          draft={composing}
          programs={wizard.programs}
          packages={wizard.packages}
          selectedEmployees={wizard.selectedEmployees}
          onWizardUpdate={setWizard}
          // Физлицо: загрузки списка и новых сотрудников нет, слушатель — он сам и добавляется в
          // каждую выбранную программу (individual-training-order).
          onImportFile={individual ? undefined : openImport}
          allowCreate={!individual}
          individual={individual}
          mode={programsMode}
          onProposeChange={onProposeChange}
          presetPersonId={
            order.dataStatus !== 'draft'
              ? undefined
              : individual
                ? (wizard.selfPersonId ?? undefined)
                : presetPersonId
          }
        />
      ) : step === DATA_STEP ? (
        <TrainingStepData
          employees={wizard.selectedEmployees}
          unnamed={unnamed}
          disabled={submitting || requesting}
          // Пока изменения состава ждут согласования, слушателей на места не назвать.
          placesLocked={changePending}
          onSaving={onSaving}
          onEmployeePatch={patchEmployee}
          onInvalidDraft={setInvalidDraft}
          onPickPlace={(key) => setPlacePicker(placeTarget(key))}
          individual={individual}
        />
      ) : (
        <TrainingStepConfirm
          draft={composing}
          filling={order.dataStatus === 'filling'}
          programs={wizard.programs}
          packages={wizard.packages}
          employees={wizard.selectedEmployees}
          complete={listenersTotal - leftToFill}
          total={listenersTotal}
        />
      )}

      {placePicker ? (
        <TrainingEmployeePickerModal
          orderId={order.id}
          target={placePicker}
          onClose={() => setPlacePicker(null)}
          onApplied={setWizard}
        />
      ) : null}

      {lastStep && changePending ? <Alert variant="info">{CHANGE_PENDING_SUBMIT_NOTICE}</Alert> : null}
      {lastStep && unavailableSelected ? (
        <Alert variant="danger">{UNAVAILABLE_SELECTION_NOTICE}</Alert>
      ) : null}
      {lastStep && requestPending ? (
        <p className="text-13 text-txt-secondary">{REQUEST_BEFORE_CONFIRM_HINT}</p>
      ) : null}
      {lastStep && !requestPending && unnamed.length > 0 ? (
        <Alert variant="info" title="Указаны не все слушатели">
          <p>{UNNAMED_PLACES_NOTICE}</p>
          <ul className="mt-1 list-disc pl-5">
            {unnamed.map((u) => (
              <li key={u.key}>
                {u.label}: {namedOfPlaces(u.named, u.places).toLowerCase()}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {step === DATA_STEP && stepError && invalidDrafts.size > 0 ? (
        <Alert variant="danger">{stepError}</Alert>
      ) : null}
      {submitError ? <Alert variant="danger">{submitError}</Alert> : null}
      {fieldErrors.length > 0 ? (
        <ul className="ml-4 list-disc text-13 text-red">
          {fieldErrors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}

      {/* На шаге «Программы и сотрудники» панель навигации закреплена у нижней границы видимой
          области, а между «Назад» и «Далее» — счётчик выбранных программ (centrika-docs!52). */}
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2',
          // Full-bleed до краёв контента: <main> имеет p-6 (24px), nw:px-8 (32px) —
          // компенсируем ровно этими отрицательными полями, иначе панель не покроет края.
          step === 1 &&
            'sticky bottom-0 z-10 -mx-6 border-t border-line bg-bg px-6 py-3 nw:-mx-8 nw:px-8',
        )}
      >
        <Button
          variant="secondary"
          disabled={step === 0}
          onClick={() => goToStep(step - 1)}
        >
          Назад
        </Button>
        {step === 1 ? (
          // Учитываются ВСЕ программы с выбранными сотрудниками, в т.ч. скрытые поиском/фильтрами
          // или свёрнутыми группами; пакеты — отдельным числом (training-order-packages).
          <span className="text-13 text-txt-secondary">
            Выбрано программ: {selectedProgramsCount}
            {selectedPackagesCount > 0 ? `, пакетов: ${selectedPackagesCount}` : null}
          </span>
        ) : null}
        {step === DATA_STEP && listenersTotal > 0 ? (
          <span className="text-13 text-txt-secondary">
            Заполнено {listenersTotal - leftToFill} из {listenersTotal}
          </span>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {lastStep && !requestPending && leftToFill > 0 ? (
            // «Осталось заполнить: N» — к первой незаполненной строке шага «Заполнение данных».
            <Button variant="secondary" onClick={goToFirstIncomplete}>
              Осталось заполнить: {leftToFill}
            </Button>
          ) : null}
          {step === 1 && programsMode === 'direct' ? (
            // К поиску каталога — без прокрутки длинного дерева (ответ заказчицы 04.11).
            <Button
              variant="secondary"
              leftIcon={<Search className="size-4 stroke-current" aria-hidden />}
              onClick={focusProgramSearch}
            >
              Поиск программ
            </Button>
          ) : null}
          {!lastStep ? (
            <Button
              // Пока заявка не отправлена, на шаге 2 главное действие — «Отправить заявку».
              variant={requestPending && step === 1 ? 'secondary' : 'primary'}
              onClick={() => goToStep(step + 1)}
            >
              Далее
            </Button>
          ) : null}
          {requestPending && step > 0 ? (
            // Черновик не по абонентскому обслуживанию: сначала заявка менеджеру (ТЗ п.1.3), данные
            // слушателей подтверждаются после неё.
            <Button
              disabled={requesting || !canRequest}
              onClick={() => void sendRequest()}
            >
              {requesting ? 'Отправка…' : 'Отправить заявку'}
            </Button>
          ) : lastStep ? (
            // Ветки «только просмотр» здесь больше нет: отправленный заказ показывается
            // экраном «Заказ отправлен» и до мастера не доходит (MR !36).
            <Button disabled={submitting || !canSubmit} onClick={() => void submit()}>
              {submitting
                ? 'Отправка…'
                : order.dataStatus === 'filling'
                  ? 'Подтвердить данные'
                  : 'Отправить заказ'}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/**
 * Шаги мастера в шапке страницы (ТЗ 26.10.2026, п.1.1, картинка 2 — справа от заголовка «Заказ
 * обучения»); на узком экране — компактно «Шаг 2 из 4 · Программы и сотрудники».
 */
export function TrainingSteps({ current, individual = false }: { current: number; individual?: boolean }) {
  const labels = trainingStepLabels(individual)
  return (
    <>
      <div className="hidden w-full nw:block">
        <Stepper steps={labels} current={current} />
      </div>
      <p className="text-13 text-txt-secondary nw:hidden">
        Шаг {current + 1} из {labels.length} · {labels[current]}
      </p>
    </>
  )
}
